import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { request } from '@reflex/ui/client/http';
import { useSocketMessage } from '@reflex/ui/client/socket';
import { useOrgKey } from '@reflex/ui/client/context';
import type { WsServerMessage } from '@reflex/shared';
import { DECISION_GATE_WS_EVENT, type DecisionGate } from '@reflex/plugin-api/services';

export type { DecisionGate } from '@reflex/plugin-api/services';

/**
 * Neutral decision-gate client. Talks only to the host `/decision-gates`
 * endpoints and the shared `DecisionGate` shape — it never imports the flow
 * system, so the inbox treats gates uniformly regardless of which producer
 * opened them.
 */
const decisionGatesApi = {
  list: () => request<DecisionGate[]>('/decision-gates'),
  submit: (gateId: string, body: { decision: string; feedback?: string }) =>
    request<{ success: boolean }>(`/decision-gates/${encodeURIComponent(gateId)}/decision`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

type OrgKey = ReturnType<typeof useOrgKey>;
const decisionGatesKey = (org: OrgKey) => ['decision-gates', org] as const;

/**
 * List pending decision gates for the active org. Refetches only when a
 * `decision_gate:updated` WS message arrives — a gate opening or resolving
 * (from the inbox, Slack, or the legacy approve route) is the one event that
 * changes the pending list, so the inbox stays live no matter which surface
 * answered without stampeding the endpoint on high-frequency flow events.
 */
export function useDecisionGates() {
  const orgKey = useOrgKey();
  const qc = useQueryClient();
  useSocketMessage(
    useCallback(
      (msg: WsServerMessage) => {
        if (msg.type === 'flow_event' && msg.flowEventType === DECISION_GATE_WS_EVENT) {
          qc.invalidateQueries({ queryKey: decisionGatesKey(orgKey) });
        }
      },
      [qc, orgKey],
    ),
  );

  return useQuery({
    queryKey: decisionGatesKey(orgKey),
    queryFn: () => decisionGatesApi.list(),
  });
}

export function useSubmitDecisionGate() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: ({
      gateId,
      decision,
      feedback,
    }: {
      gateId: string;
      decision: string;
      feedback?: string;
    }) => decisionGatesApi.submit(gateId, { decision, feedback }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: decisionGatesKey(orgKey) });
    },
  });
}
